import { adminEntityDataApi, adminMetaEntityApi } from '@/api/dashboard';
import { siteApi } from '@/api/site';
import { toCreationCampaignBlueprintsFromSite } from '@/lib/create/campaignBlueprints';
import type { CreationCampaignBlueprint } from '@/types/creation';
import type { EntityDataResponse } from '@/types/dashboard';
import type {
  SiteCampaignBlueprint,
  SiteCampaignBlueprintSummary,
  SiteResource,
} from '@/types/site';

const DEFAULT_BLUEPRINT_STATUS = 'Ready';
const FORM_SUBMISSION_PAGE_SIZE = 100;
const DEFAULT_FORM_SUBMISSION_ENTITY_NAMES = ['form_submission', 'diagnostic_call_booking'];
const FORM_ENTITY_NAME_HINTS = [
  'form',
  'submission',
  'booking',
  'contact',
  'lead',
  'inquiry',
  'enquiry',
  'appointment',
  'prescription',
];
const SUBMISSION_METADATA_KEYS = new Set(
  [
    'answers',
    'browser',
    'channel',
    'createdAt',
    'createTime',
    'device',
    'domain',
    'fields',
    'form',
    'formData',
    'formFields',
    'formId',
    'formName',
    'formTitle',
    'ip',
    'landingPage',
    'page',
    'pageName',
    'pagePath',
    'pageTitle',
    'path',
    'payload',
    'projectId',
    'referrer',
    'sessionId',
    'siteId',
    'source',
    'submitTime',
    'submittedAt',
    'submittedFrom',
    'tenantId',
    'time',
    'url',
    'userAgent',
    'userId',
    'utmCampaign',
    'utmContent',
    'utmMedium',
    'utmSource',
    'utmTerm',
    'values',
    'visitorId',
  ].map((key) => key.toLowerCase().replace(/[^a-z0-9]/g, '')),
);

export interface SiteFormSubmissionPageContext {
  instanceToken?: string;
  path?: string;
  tenantId?: number;
  title?: string;
}

export interface SiteFormSubmissionField {
  key: string;
  label: string;
  value: string;
}

export interface SiteFormSubmissionItem {
  contact: string;
  fields: SiteFormSubmissionField[];
  form: string;
  id: string;
  page: string;
  path: string;
  source: string;
  submittedAt: string;
  tenantId?: number;
  time: string;
  title: string;
}

export interface LoadSiteFormSubmissionsParams {
  instanceToken?: string;
  pageContexts?: SiteFormSubmissionPageContext[];
  pageSize?: number;
  tenantIds?: number[];
}

const parseJsonRecord = (value?: string | null): Record<string, unknown> => {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const normalizeDataKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const toText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item))
      .filter(Boolean)
      .join(', ');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const readSubmissionValue = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  }

  const wanted = keys.map(normalizeDataKey);
  const match = Object.entries(record).find(([key]) => wanted.includes(normalizeDataKey(key)));
  return match?.[1];
};

const formatSubmissionLabel = (key: string) =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isValidDate = (value: Date) => Number.isFinite(value.getTime());

const formatSubmissionDate = (value: unknown) => {
  const text = toText(value);
  if (!text) return '--';

  const parsed = new Date(text);
  if (!isValidDate(parsed)) return text;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const date = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  const time = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
  }).format(parsed);

  if (date === today) return `Today ${time}`;
  if (date === today - 24 * 60 * 60 * 1000) return `Yesterday ${time}`;

  const day = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: parsed.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  }).format(parsed);
  return `${day} ${time}`;
};

const getConfiguredFormSubmissionEntityNames = () => {
  const configuredNames = String(import.meta.env.VITE_FORM_SUBMISSION_ENTITY_NAME || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  return [...new Set(configuredNames)];
};

const normalizeTenantIds = (tenantIds?: number[]) => [
  ...new Set((tenantIds || []).map(Number).filter((id) => Number.isFinite(id) && id > 0)),
];

const normalizeEntityName = (value: unknown) => String(value || '').trim();

const isLikelyFormEntity = (entityName: string) => {
  const normalized = entityName.toLowerCase();
  return FORM_ENTITY_NAME_HINTS.some((hint) => normalized.includes(hint));
};

const isUsableFormEntity = (entity: {
  entityName?: string;
  dataScope?: string;
  status?: number;
}) => {
  const entityName = normalizeEntityName(entity.entityName);
  if (!entityName) return false;
  if (entity.status !== undefined && Number(entity.status) !== 1) return false;

  const dataScope = String(entity.dataScope || '')
    .trim()
    .toUpperCase();
  return !dataScope || dataScope === 'USER' || isLikelyFormEntity(entityName);
};

const discoverFormSubmissionEntityNames = async ({
  instanceToken,
  tenantIds,
}: {
  instanceToken?: string;
  tenantIds: number[];
}) => {
  const response = await adminMetaEntityApi.findListByTenantIds(
    {
      status: 1,
      tenantIds: instanceToken ? undefined : tenantIds,
    },
    instanceToken,
  );

  return [
    ...new Set(
      (response || [])
        .filter(isUsableFormEntity)
        .map((entity) => normalizeEntityName(entity.entityName))
        .filter(Boolean),
    ),
  ];
};

const createPageContextLookup = (pageContexts?: SiteFormSubmissionPageContext[]) =>
  (pageContexts || []).reduce<Record<number, SiteFormSubmissionPageContext>>((lookup, context) => {
    const tenantId = Number(context.tenantId);
    if (Number.isFinite(tenantId) && tenantId > 0 && !lookup[tenantId]) {
      lookup[tenantId] = context;
    }
    return lookup;
  }, {});

const normalizeFieldEntry = (key: string, value: unknown): SiteFormSubmissionField | null => {
  const displayValue = toText(value);
  if (!displayValue) return null;

  return {
    key,
    label: formatSubmissionLabel(key),
    value: displayValue,
  };
};

const normalizeFieldArray = (value: unknown): SiteFormSubmissionField[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item, index) => {
      if (!isRecord(item)) return normalizeFieldEntry(`field_${index + 1}`, item);

      const key =
        toText(readSubmissionValue(item, ['key', 'name', 'fieldName', 'field_name', 'id'])) ||
        `field_${index + 1}`;
      const label =
        toText(readSubmissionValue(item, ['label', 'displayName', 'display_name', 'title'])) ||
        formatSubmissionLabel(key);
      const fieldValue = readSubmissionValue(item, [
        'value',
        'fieldValue',
        'field_value',
        'answer',
        'fieldAnswer',
        'field_answer',
        'text',
      ]);
      const displayValue = toText(fieldValue);
      if (!displayValue) return null;

      return {
        key,
        label,
        value: displayValue,
      };
    })
    .filter((item): item is SiteFormSubmissionField => item !== null);
};

const extractSubmissionFields = (data: Record<string, unknown>) => {
  const nestedFields = readSubmissionValue(data, [
    'fields',
    'formFields',
    'form_fields',
    'formData',
    'form_data',
    'submissionData',
    'submission_data',
    'payload',
    'values',
    'answers',
  ]);

  if (Array.isArray(nestedFields)) return normalizeFieldArray(nestedFields);

  const fieldRecord = isRecord(nestedFields)
    ? nestedFields
    : Object.fromEntries(
        Object.entries(data).filter(
          ([key]) => !SUBMISSION_METADATA_KEYS.has(normalizeDataKey(key)),
        ),
      );

  return Object.entries(fieldRecord)
    .map(([key, value]) => normalizeFieldEntry(key, value))
    .filter((item): item is SiteFormSubmissionField => item !== null);
};

const createFieldLookup = (fields: SiteFormSubmissionField[]) =>
  fields.reduce<Record<string, string>>((lookup, field) => {
    lookup[field.key] = field.value;
    lookup[field.label] = field.value;
    lookup[normalizeDataKey(field.key)] = field.value;
    lookup[normalizeDataKey(field.label)] = field.value;
    return lookup;
  }, {});

const readFieldLookupValue = (lookup: Record<string, string>, keys: string[]) => {
  for (const key of keys) {
    const direct = lookup[key];
    if (direct) return direct;

    const normalized = lookup[normalizeDataKey(key)];
    if (normalized) return normalized;
  }

  return '';
};

const formatSubmissionSource = (data: Record<string, unknown>) => {
  const direct = toText(
    readSubmissionValue(data, [
      'source',
      'trafficSource',
      'traffic_source',
      'channel',
      'utmSource',
      'utm_source',
      'referrer',
      'referer',
    ]),
  );
  const campaign = toText(readSubmissionValue(data, ['utmCampaign', 'utm_campaign', 'campaign']));

  if (direct && campaign && !direct.includes(campaign)) return `${direct} / ${campaign}`;
  return direct || campaign || '--';
};

const normalizeFormSubmission = (
  item: EntityDataResponse,
  index: number,
  pageContextLookup: Record<number, SiteFormSubmissionPageContext>,
): SiteFormSubmissionItem => {
  const data = isRecord(item.data) ? item.data : {};
  const pageContext = pageContextLookup[item.tenantId] || {};
  const fields = extractSubmissionFields(data);
  const fieldLookup = createFieldLookup(fields);
  const firstName = readFieldLookupValue(fieldLookup, ['first_name', 'firstName']);
  const lastName = readFieldLookupValue(fieldLookup, ['last_name', 'lastName']);
  const title =
    readFieldLookupValue(fieldLookup, [
      'name',
      'full_name',
      'fullName',
      'contact_name',
      'contactName',
      'contact',
    ]) ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    toText(
      readSubmissionValue(data, [
        'name',
        'full_name',
        'fullName',
        'contact_name',
        'contactName',
        'contact',
      ]),
    ) ||
    `Submission #${String(index + 1).padStart(2, '0')}`;
  const email =
    readFieldLookupValue(fieldLookup, [
      'email',
      'work_email',
      'workEmail',
      'business_email',
      'businessEmail',
      'requester_email',
      'requesterEmail',
    ]) ||
    toText(
      readSubmissionValue(data, [
        'email',
        'work_email',
        'workEmail',
        'business_email',
        'businessEmail',
        'requester_email',
        'requesterEmail',
      ]),
    );
  const company =
    readFieldLookupValue(fieldLookup, [
      'company',
      'company_name',
      'companyName',
      'organization',
      'account',
    ]) ||
    toText(
      readSubmissionValue(data, [
        'company',
        'company_name',
        'companyName',
        'organization',
        'account',
      ]),
    );
  const phone =
    readFieldLookupValue(fieldLookup, ['phone', 'mobile', 'tel', 'phone_number', 'phoneNumber']) ||
    toText(readSubmissionValue(data, ['phone', 'mobile', 'tel', 'phone_number', 'phoneNumber']));
  const form =
    toText(
      readSubmissionValue(data, [
        'form',
        'formName',
        'form_name',
        'formTitle',
        'form_title',
        'formId',
        'form_id',
      ]),
    ) || 'Lead form';
  const page =
    toText(
      readSubmissionValue(data, [
        'page',
        'pageName',
        'page_name',
        'pageTitle',
        'page_title',
        'submittedFrom',
        'submitted_from',
        'landingPage',
        'landing_page',
      ]),
    ) ||
    pageContext.title ||
    'Landing page';
  const path =
    toText(
      readSubmissionValue(data, [
        'path',
        'pagePath',
        'page_path',
        'pathname',
        'urlPath',
        'url_path',
        'landingPagePath',
        'landing_page_path',
      ]),
    ) ||
    pageContext.path ||
    '/';
  const submittedAt =
    toText(
      readSubmissionValue(data, [
        'submittedAt',
        'submitted_at',
        'submitTime',
        'submit_time',
        'createdAt',
        'created_at',
        'time',
      ]),
    ) ||
    item.createTime ||
    item.modifyTime ||
    '';

  return {
    contact: [email, company, phone].filter(Boolean).join(' / ') || form,
    fields,
    form,
    id: item.entityInstanceId || `${item.tenantId}-${item.createTime || index}`,
    page,
    path,
    source: formatSubmissionSource(data),
    submittedAt,
    tenantId: item.tenantId,
    time: formatSubmissionDate(submittedAt),
    title,
  };
};

const createFallbackBlueprint = (
  summary: SiteCampaignBlueprintSummary,
  index: number,
): CreationCampaignBlueprint => ({
  localId: String(summary.id),
  id: summary.id,
  siteId: summary.siteId,
  title: summary.title || `Blueprint ${index + 1}`,
  summary: summary.summary || 'A site campaign blueprint is ready for this direction.',
  status: summary.status || DEFAULT_BLUEPRINT_STATUS,
  kicker: `Blueprint ${String(index + 1).padStart(2, '0')}`,
  metrics: [],
  tags: [],
  content: {
    title: summary.title,
    summary: summary.summary,
    status: summary.status || DEFAULT_BLUEPRINT_STATUS,
  },
  contentJson: '',
  sourceTaskCode: summary.sourceTaskCode,
  contentSchema: summary.contentSchema,
  schemaVersion: summary.schemaVersion,
  sortOrder: summary.sortOrder,
  linkedLandingPageCount: summary.linkedLandingPageCount,
  linkedAdCampaignCount: summary.linkedAdCampaignCount,
  linkedItemCount: summary.linkedItemCount,
  createTime: summary.createTime,
  modifyTime: summary.modifyTime,
});

export const parseDashboardJsonRecord = parseJsonRecord;

export const getBlueprintLinkedCount = (blueprint: {
  linkedItemCount?: number;
  linkedLandingPageCount?: number;
  linkedAdCampaignCount?: number;
}) =>
  blueprint.linkedItemCount ??
  (blueprint.linkedLandingPageCount || 0) + (blueprint.linkedAdCampaignCount || 0);

export const getBlueprintMetricValues = (blueprint: CreationCampaignBlueprint, count = 3) =>
  blueprint.metrics
    .slice(0, count)
    .map((item) => item.value.trim())
    .filter(Boolean);

export const loadSiteResources = async (siteId: number) => {
  const response = await siteApi.getResourceList({
    pageNumber: 1,
    pageSize: 200,
    siteId,
  });
  return response.list || [];
};

export const loadSiteBlueprints = async (siteId: number) => {
  const response = await siteApi.getCampaignBlueprintList({
    pageNumber: 1,
    pageSize: 100,
    siteId,
  });
  const summaries = response.list || [];

  if (!summaries.length) return [];

  const detailResults = await Promise.allSettled(
    summaries.map((summary) =>
      siteApi.getCampaignBlueprintDetail({
        siteId,
        id: summary.id,
      }),
    ),
  );

  return summaries.map((summary, index) => {
    const detailResult = detailResults[index];
    if (detailResult?.status === 'fulfilled' && detailResult.value) {
      const [normalized] = toCreationCampaignBlueprintsFromSite([
        detailResult.value as SiteCampaignBlueprint,
      ]);
      if (normalized) return normalized;
    }

    return createFallbackBlueprint(summary, index);
  });
};

export const loadSiteFormSubmissions = async ({
  instanceToken,
  pageContexts,
  pageSize = FORM_SUBMISSION_PAGE_SIZE,
  tenantIds,
}: LoadSiteFormSubmissionsParams) => {
  const normalizedTenantIds = normalizeTenantIds(tenantIds);

  if (!instanceToken && normalizedTenantIds.length === 0) {
    return {
      entityName: '',
      submissions: [] as SiteFormSubmissionItem[],
      total: 0,
      totalPages: 0,
    };
  }

  const pageContextLookup = createPageContextLookup(pageContexts);
  const configuredEntityNames = getConfiguredFormSubmissionEntityNames();
  let entityNames = configuredEntityNames;

  try {
    const discoveredEntityNames = await discoverFormSubmissionEntityNames({
      instanceToken,
      tenantIds: normalizedTenantIds,
    });
    if (discoveredEntityNames.length) {
      entityNames = [...new Set([...configuredEntityNames, ...discoveredEntityNames])];
    }
  } catch {
    // Keep the configured/default entity names as a compatibility fallback.
  }

  if (!entityNames.length) {
    entityNames = DEFAULT_FORM_SUBMISSION_ENTITY_NAMES;
  }

  const results = await Promise.allSettled(
    entityNames.map((entityName) =>
      adminEntityDataApi.findListByPageByTenantIds(
        {
          entityName,
          pageNumber: 1,
          pageSize,
          sorts: [{ field: 'createTime', order: 'DESC' }],
          tenantIds: instanceToken ? undefined : normalizedTenantIds,
        },
        instanceToken,
      ),
    ),
  );
  const successfulResults = results.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : [],
  );
  const failedResults = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  );

  if (!successfulResults.length && failedResults.length) {
    throw failedResults[failedResults.length - 1].reason;
  }

  const submissionsById = new Map<string, EntityDataResponse>();
  successfulResults.forEach((response) => {
    (response.list || []).forEach((item) => {
      const key = `${item.tenantId}:${item.entityInstanceId || item.createTime || ''}`;
      if (!submissionsById.has(key)) submissionsById.set(key, item);
    });
  });
  const list = [...submissionsById.values()].sort((left, right) => {
    const leftTime = new Date(left.createTime || left.modifyTime || 0).getTime();
    const rightTime = new Date(right.createTime || right.modifyTime || 0).getTime();
    return rightTime - leftTime;
  });

  return {
    entityName: entityNames.join(','),
    submissions: list.map((item, index) => normalizeFormSubmission(item, index, pageContextLookup)),
    total: successfulResults.reduce((total, response) => total + (response.total || 0), 0),
    totalPages: Math.max(1, ...successfulResults.map((response) => response.totalPages || 1)),
  };
};

export const getResourceCounts = (resources: SiteResource[]) =>
  resources.reduce(
    (counts, resource) => {
      const subtype = String(resource.resourceSubtype || '').toLowerCase();
      const schema = String(resource.contentSchema || '').toLowerCase();
      const mimeType = String(resource.mimeType || '').toLowerCase();
      const url = String(resource.primaryUrl || '').toLowerCase();

      if (
        subtype === 'pdf' ||
        mimeType === 'application/pdf' ||
        schema.includes('asset.pdf') ||
        url.endsWith('.pdf')
      ) {
        counts.pdf += 1;
      } else if (
        subtype === 'url' ||
        subtype === 'official-website' ||
        subtype === 'internal-link' ||
        subtype === 'reference-website' ||
        schema.includes('asset.url')
      ) {
        counts.url += 1;
      } else {
        counts.image += 1;
      }

      return counts;
    },
    { image: 0, pdf: 0, url: 0 },
  );
